import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../ui/sheet';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Separator } from '../../../ui/separator';
import { Flag, CheckCircle2, MapPin, Calendar, Home, Car } from 'lucide-react';
import { prettyDate } from '../surveyUiUtils';

const Row = ({ label, value, mono }) => (
  <div className='flex items-start justify-between gap-4 py-1.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    <span className={`text-sm text-right ${mono ? 'font-mono tabular-nums' : ''}`}>{value ?? '—'}</span>
  </div>
);

const MODE_LABEL = {
  walk: 'Walk', cycle: 'Cycle', ebike: 'E-Bike', car: 'Car',
  motorbike: 'Motorbike', bus: 'Bus', coach: 'Coach', train: 'Train',
  taxi: 'Taxi', van: 'Van',
};

const RespondentDetailSheet = ({ open, onOpenChange, response, invitation, facilityName, onFlag, onExclude, loading }) => {
  if (!response) return null;

  const rd = response.responseData || {};
  const legs = Array.isArray(rd.commute_legs) ? rd.commute_legs.filter((l) => l.leg_mode) : [];
  const workedFullPeriod = String(rd.worked_full_reporting_period || '').toLowerCase() === 'yes';
  const employeeName = invitation?.employeeName || '—';
  const employeeEmail = invitation?.employeeEmail;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='sm:max-w-lg overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>{employeeName}</SheetTitle>
          <SheetDescription>Raw survey answers and response audit.</SheetDescription>
        </SheetHeader>

        <div className='flex items-center gap-2 flex-wrap mt-4 mb-5'>
          {facilityName && (
            <Badge variant='info' size='default'>
              <MapPin size={10} strokeWidth={1.5} />
              {facilityName}
            </Badge>
          )}
          <Badge variant={workedFullPeriod ? 'success' : 'warning'} size='default'>
            <CheckCircle2 size={10} strokeWidth={1.5} />
            {workedFullPeriod ? 'Full period' : `From ${rd.employment_start_month || 'part period'}`}
          </Badge>
          {response.submittedAt && (
            <Badge variant='outline' size='default'>
              <Calendar size={10} strokeWidth={1.5} />
              {prettyDate(response.submittedAt)}
            </Badge>
          )}
        </div>

        <div className='rounded-lg border border-border p-4 mb-5'>
          <div className='font-display text-sm mb-2'>Working pattern</div>
          <Row label='Days per week' value={rd.days_per_week} />
          <Row label='WFH days per week' value={rd.wfh_days_per_week} />
          {rd.commute_days_per_week != null && (
            <Row label='Commute days per week' value={rd.commute_days_per_week} />
          )}
          {employeeEmail && <Row label='Email' value={employeeEmail} />}
        </div>

        <div className='rounded-lg border border-border p-4 mb-5'>
          <div className='flex items-center gap-2 font-display text-sm mb-2'>
            <Home size={14} strokeWidth={1.5} className='text-accent' />
            WFH setup
          </div>
          {rd.wfh_laptop_count != null && <Row label='Laptops' value={rd.wfh_laptop_count} mono />}
          {rd.wfh_desktop_count != null && <Row label='Desktops' value={rd.wfh_desktop_count} mono />}
          {rd.wfh_external_monitor_count != null && <Row label='External monitors' value={rd.wfh_external_monitor_count} mono />}
          {rd.wfh_video_call_hours_band && <Row label='Video call hours / week' value={rd.wfh_video_call_hours_band} />}
          {rd.wfh_extra_lighting_usage && <Row label='Extra lighting usage' value={rd.wfh_extra_lighting_usage} />}
          {rd.wfh_light_bulb_type && <Row label='Bulb type' value={rd.wfh_light_bulb_type} />}
          <Separator className='my-2' />
          {rd.wfh_heating_fuel && <Row label='Heating fuel' value={rd.wfh_heating_fuel} />}
          {rd.wfh_heating_on_while_wfh && <Row label='Heating on while WFH' value={rd.wfh_heating_on_while_wfh} />}
          {rd.wfh_heating_hours_typical_day != null && <Row label='Heating hrs / day' value={rd.wfh_heating_hours_typical_day} mono />}
          {rd.wfh_heating_months_per_year != null && <Row label='Heating months / year' value={rd.wfh_heating_months_per_year} mono />}
          {rd.wfh_heated_area_scope && <Row label='Heated area scope' value={rd.wfh_heated_area_scope} />}
          {rd.wfh_active_cooling === 'yes' && (
            <>
              <Separator className='my-2' />
              {rd.wfh_cooling_hours_typical_day != null && <Row label='Cooling hrs / day' value={rd.wfh_cooling_hours_typical_day} mono />}
              {rd.wfh_cooling_months_per_year != null && <Row label='Cooling months / year' value={rd.wfh_cooling_months_per_year} mono />}
              {rd.wfh_cooled_area_scope && <Row label='Cooled area scope' value={rd.wfh_cooled_area_scope} />}
            </>
          )}
          <Separator className='my-2' />
          {rd.home_electricity_renewable_tariff && (
            <div className='mt-2'>
              <Badge variant='outline' size='sm'>
                Renewable tariff: {rd.home_electricity_renewable_tariff}
              </Badge>
            </div>
          )}
          {rd.home_self_generation === 'yes' && (
            <div className='mt-2'>
              <Badge variant='outline' size='sm'>
                Self-generation: {rd.home_self_generation_daytime_share ?? 'yes'}
              </Badge>
            </div>
          )}
        </div>

        {legs.length > 0 && (
          <div className='rounded-lg border border-border p-4 mb-5'>
            <div className='flex items-center gap-2 font-display text-sm mb-2'>
              <Car size={14} strokeWidth={1.5} className='text-accent' />
              Commuting legs
            </div>
            {legs.map((leg, i) => (
              <div key={i} className='flex items-center justify-between py-2 border-b border-border last:border-b-0'>
                <div>
                  <div className='text-xs text-muted-foreground font-mono'>Leg {i + 1}</div>
                  <div className='text-sm font-medium'>{MODE_LABEL[leg.leg_mode] || leg.leg_mode}</div>
                  {leg.leg_car_fuel && <div className='text-xs text-muted-foreground'>{leg.leg_car_fuel}</div>}
                </div>
                <div className='text-right'>
                  {leg.leg_distance_km_one_way != null && (
                    <div className='font-mono text-sm tabular-nums'>{leg.leg_distance_km_one_way} km / one way</div>
                  )}
                </div>
              </div>
            ))}
            <Separator className='my-2' />
            <Row label='Commute days / week' value={rd.commute_days_per_week} mono />
          </div>
        )}

        <div className='flex items-center justify-end gap-2 mt-6'>
          <Button variant='outline' size='sm' onClick={onFlag} loading={Boolean(loading?.flagResponse)}>
            <Flag size={14} strokeWidth={1.5} />
            Flag for review
          </Button>
          <Button variant='destructive' size='sm' onClick={onExclude} loading={Boolean(loading?.flagResponse)}>
            Exclude from calc
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default RespondentDetailSheet;
